import Keycloak from 'keycloak-js';

const DEV_ADMIN_USER_ID = '00000000-0000-0000-0000-0000000000ad';

export interface SessionUser {
  authenticated: boolean;
  isOrqForgeAdmin: boolean;
  userId: string | null;
  firstName: string | null;
  lastName: string | null;
  authDisabled: boolean;
}

const empty: SessionUser = {
  authenticated: false,
  isOrqForgeAdmin: false,
  userId: null,
  firstName: null,
  lastName: null,
  authDisabled: false,
};

let keycloak: Keycloak | null = null;
let authDisabled = false;
let idpHint = '';
let user: SessionUser = empty;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSessionUser(): SessionUser {
  return user;
}

export function identityHeaders(): Record<string, string> {
  if (!authDisabled) return {};
  return {
    'x-user-id': DEV_ADMIN_USER_ID,
    'x-roles': 'orqforge_admin',
    'x-email': 'admin@localhost',
  };
}

export async function initDisabledAuth(): Promise<void> {
  authDisabled = true;
  user = {
    authenticated: true,
    isOrqForgeAdmin: true,
    userId: DEV_ADMIN_USER_ID,
    firstName: 'Admin',
    lastName: null,
    authDisabled: true,
  };
  emit();
}

export async function initKeycloak(config: {
  url: string;
  realm: string;
  clientId: string;
  idpHint?: string;
}): Promise<boolean> {
  idpHint = config.idpHint ?? '';
  keycloak = new Keycloak({ url: config.url, realm: config.realm, clientId: config.clientId });
  keycloak.onTokenExpired = () => {
    void keycloak?.login(idpHint ? { idpHint } : undefined);
  };
  const authenticated = await keycloak.init({
    onLoad: 'check-sso',
    checkLoginIframe: false,
    pkceMethod: 'S256',
  });
  if (!authenticated) {
    await keycloak.login(idpHint ? { idpHint } : undefined);
    return false;
  }
  const parsed = keycloak.tokenParsed as Record<string, unknown> | undefined;
  let firstName: string | null = (parsed?.given_name as string) ?? null;
  const lastName = (parsed?.family_name as string) ?? null;
  if (!firstName && !lastName) {
    firstName = (parsed?.name as string) || (parsed?.preferred_username as string) || null;
  }
  const realmRoles: string[] = keycloak.tokenParsed?.realm_access?.roles ?? [];
  user = {
    authenticated: true,
    isOrqForgeAdmin: realmRoles.includes('orqforge_admin'),
    userId: keycloak.tokenParsed?.sub ?? null,
    firstName,
    lastName,
    authDisabled: false,
  };
  emit();
  return true;
}

export async function getToken(): Promise<string | null> {
  if (authDisabled || !keycloak) return null;
  try {
    await keycloak.updateToken(30);
    return keycloak.token ?? null;
  } catch {
    return null;
  }
}

export function login(): void {
  void keycloak?.login(idpHint ? { idpHint } : undefined);
}

export function logout(): void {
  if (authDisabled) return;
  void keycloak?.logout();
}

export function displayName(): string {
  const { firstName, lastName } = user;
  if (firstName && lastName) return `${firstName} ${lastName}`;
  return firstName ?? lastName ?? 'User';
}
